//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMemo, useState } from 'react';

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { SocialNetwork } from '@models/SocialNetwork';
import { usePostDetailsFormContext } from '@universal-scheduling/contexts/scheduler/PostDetailsFormContext';
import { useSchedulerContext } from '@universal-scheduling/contexts/scheduler/SchedulerContext';
import { IPostDetailsTabData } from '@universal-scheduling/contexts/scheduler/schedulerSharedStateModels';
import { TSchedulerFormState } from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { arrayUnorderedEquals } from '@utils/arrayUtils';
import { isDefined } from '@utils/filterPredicates';

export const getSocialNetworksFromTabData = (
  postDetailsTabData: IPostDetailsTabData[],
): SocialNetwork[] =>
  postDetailsTabData
    .filter((tabData) => tabData.isActive)
    .map((tabData) => tabData.socialNetwork);

export const useSchedulerSocialNetworksFromTabData = (): SocialNetwork[] => {
  const { postDetailsTabData } = useSchedulerContext();

  return useMemo(
    () => getSocialNetworksFromTabData(postDetailsTabData),
    [postDetailsTabData],
  );
};

export const usePostDetailsFormPostTypesAndFormIds = () => {
  const [postTypesAndFormIds, setPostTypesAndFormIds] = useState<
    { postType: UniversalPostType; formId: string | undefined }[]
  >([]);

  const { getValues } = usePostDetailsFormContext();
  const formValues = getValues();
  const formData = Object.values(formValues) as TSchedulerFormState[];

  const currentPostTypes = formData.map(({ postType }) => postType);
  if (
    !arrayUnorderedEquals(
      currentPostTypes,
      postTypesAndFormIds.map(({ postType }) => postType),
    )
  ) {
    const currentPostTypesAndFormIds = formData.map((data) => ({
      postType: data.postType,
      formId: data.formId,
    }));
    setPostTypesAndFormIds(currentPostTypesAndFormIds);
  }

  return postTypesAndFormIds;
};

export const useSchedulerPostTypesFromTabData = (): UniversalPostType[] => {
  const { postDetailsTabData } = useSchedulerContext();
  const postTypesAndFormIds = usePostDetailsFormPostTypesAndFormIds();
  const activeTabFormIds = useMemo(
    () =>
      postDetailsTabData
        .filter(({ isActive }) => isActive)
        .map(({ formId }) => formId),
    [postDetailsTabData],
  );

  return useMemo(
    () =>
      activeTabFormIds
        .map(
          (formId) =>
            postTypesAndFormIds.find((form) => form.formId === formId)
              ?.postType,
        )
        .filter(isDefined),
    [activeTabFormIds, postTypesAndFormIds],
  );
};
